import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionsApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate, formatEnergy, formatCurrency, formatDuration } from '@/lib/utils';
import { Clock, Filter, ChevronRight, BatteryCharging, Zap, DollarSign, RefreshCw } from 'lucide-react';
import {
  Page, GlassCard, PageHeader, StatCard, SectionHeading, EmptyState, ErrorState, PageSkeleton,
  LoadingRows, SearchField, errorMessage
} from '@/components/glass/surfaces';

const PAGE_SIZE = 50;

const SessionsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState([]);
  const [filteredSessions, setFilteredSessions] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await sessionsApi.getAll({ limit });
        if (cancelled) return;
        setSessions(response.data || []);
        setError('');
      } catch (fetchError) {
        if (cancelled) return;
        console.error('Failed to fetch sessions:', fetchError);
        setError(errorMessage(fetchError, 'Failed to load sessions'));
      } finally {
        if (!cancelled) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [limit, reloadToken]);

  useEffect(() => {
    let filtered = sessions;
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((session) =>
        (session.charge_point_id || '').toLowerCase().includes(searchLower) ||
        (session.id_tag || '').toLowerCase().includes(searchLower) ||
        String(session.transaction_id ?? '').includes(searchLower)
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((session) => (statusFilter === 'active' ? session.is_active : !session.is_active));
    }
    setFilteredSessions(filtered);
  }, [sessions, search, statusFilter]);

  const activeSessions = sessions.filter((session) => session.is_active).length;
  const totalEnergy = sessions.reduce((sum, session) => sum + (session.energy_kwh || 0), 0);
  const totalRevenue = sessions.reduce((sum, session) => sum + (session.cost || 0), 0);
  const isFiltered = Boolean(search) || statusFilter !== 'all';
  const mayHaveMore = sessions.length >= limit;

  if (loading && sessions.length === 0 && !error) {
    return <PageSkeleton testId="sessions-loading" />;
  }

  return (
    <Page testId="sessions-page">
      <PageHeader
        title="Sessions"
        description="Charging transactions, energy, and cost."
        actions={(
          <Button
            type="button"
            variant="outline"
            className="rounded-full"
            onClick={() => setReloadToken((value) => value + 1)}
            data-testid="refresh-sessions-btn"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        )}
      />

      {error ? (
        <ErrorState
          title={sessions.length ? 'Session list may be out of date' : 'Could not load sessions'}
          message={error}
          onRetry={() => { setLoading(sessions.length === 0); setReloadToken((value) => value + 1); }}
        />
      ) : null}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard label="Loaded sessions" value={sessions.length} hint={mayHaveMore ? `Latest ${limit}` : 'All loaded'} icon={Clock} tone="accent" />
        <StatCard label="Active now" value={activeSessions} icon={BatteryCharging} tone="info" />
        <StatCard label="Energy" value={formatEnergy(totalEnergy)} hint="In loaded sessions" icon={Zap} tone="warning" />
        <StatCard label="Revenue" value={formatCurrency(totalRevenue)} hint="In loaded sessions" icon={DollarSign} tone="success" />
      </div>

      <GlassCard tone="subtle" className="p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row">
          <SearchField
            value={search}
            onChange={setSearch}
            placeholder="Search by charger, ID tag, or transaction"
            testId="search-input"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full rounded-full bg-background/70 sm:w-44" data-testid="status-filter">
              <span className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                <SelectValue placeholder="Status" />
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </GlassCard>

      {error && sessions.length === 0 ? null : (
      <GlassCard className="p-5 sm:p-6">
        <SectionHeading
          title="Session history"
          description={isFiltered ? `${filteredSessions.length} of ${sessions.length} shown` : `${sessions.length} loaded`}
        />
        {loading ? (
          <LoadingRows />
        ) : filteredSessions.length === 0 ? (
          <div data-testid="no-sessions">
            <EmptyState
              icon={Clock}
              title={isFiltered ? 'No sessions match' : 'No sessions yet'}
              description={isFiltered ? 'Try a different search or status.' : 'Sessions appear when a charger starts a transaction.'}
              action={isFiltered ? (
                <Button type="button" variant="outline" className="rounded-full" onClick={() => { setSearch(''); setStatusFilter('all'); }}>
                  Clear filters
                </Button>
              ) : null}
            />
          </div>
        ) : (
          <>
            <div className="hidden md:block">
              <Table className="min-w-[980px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Transaction</TableHead>
                    <TableHead>Charger</TableHead>
                    <TableHead className="whitespace-nowrap">ID tag</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="whitespace-nowrap">Start time</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Energy</TableHead>
                    <TableHead>Cost</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSessions.map((session) => (
                    <TableRow
                      key={session.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/sessions/${session.id}`)}
                      data-testid={`session-row-${session.transaction_id}`}
                    >
                      <TableCell className="whitespace-nowrap font-mono font-medium">#{session.transaction_id}</TableCell>
                      <TableCell className="font-mono">{session.charge_point_id}</TableCell>
                      <TableCell className="font-mono text-muted-foreground">{session.id_tag || '-'}</TableCell>
                      <TableCell>
                        <StatusBadge status={session.is_active ? 'Charging' : 'Finished'} variant="dot" />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-muted-foreground">{formatDate(session.start_timestamp)}</TableCell>
                      <TableCell className="whitespace-nowrap">{formatDuration(session.start_timestamp, session.stop_timestamp)}</TableCell>
                      <TableCell className="whitespace-nowrap font-mono">{formatEnergy(session.energy_kwh)}</TableCell>
                      <TableCell className="whitespace-nowrap font-mono">{formatCurrency(session.cost, session.currency)}</TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="space-y-3 md:hidden">
              {filteredSessions.map((session) => (
                <button
                  key={session.id}
                  type="button"
                  className="w-full rounded-2xl bg-black/[0.03] p-4 text-left dark:bg-white/[0.04]"
                  onClick={() => navigate(`/sessions/${session.id}`)}
                  data-testid={`session-card-${session.transaction_id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-mono font-medium">#{session.transaction_id}</p>
                      <p className="truncate text-sm text-muted-foreground">{session.charge_point_id}</p>
                    </div>
                    <StatusBadge status={session.is_active ? 'Charging' : 'Finished'} variant="dot" />
                  </div>
                  <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <dt className="text-xs text-muted-foreground">Energy</dt>
                      <dd className="font-mono">{formatEnergy(session.energy_kwh)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Cost</dt>
                      <dd className="font-mono">{formatCurrency(session.cost, session.currency)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">Duration</dt>
                      <dd>{formatDuration(session.start_timestamp, session.stop_timestamp)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-muted-foreground">ID tag</dt>
                      <dd className="truncate font-mono">{session.id_tag || '-'}</dd>
                    </div>
                  </dl>
                </button>
              ))}
            </div>

            {mayHaveMore ? (
              <div className="mt-4 flex justify-center">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full"
                  disabled={loadingMore}
                  onClick={() => {
                    setLoadingMore(true);
                    setLimit((value) => value + PAGE_SIZE);
                  }}
                >
                  {loadingMore ? 'Loading...' : 'Load more sessions'}
                </Button>
              </div>
            ) : null}
          </>
        )}
      </GlassCard>
      )}
    </Page>
  );
};

export default SessionsPage;
